import * as gql from 'gql-query-builder';

import { GetInstrumentPriceBarsPayload } from './dto';

import { Periodicity } from 'types';

import {
  contractsAndTokensListFields,
  getInstrumentsFields,
  getCurrenciesField,
  instrumentsPricesFields,
  subscribeOrderbookFields,
  instrumentPriceBarsFields,
  subscribeInstrumentPriceFields,
  getOrdersFields,
} from './fields';

export enum TradeOperations {
  Config = 'config',
  Instruments = 'instruments',
  InstrumentPriceBars = 'instrument_price_bars',
  InstrumentPriceBar = 'instrument_price_bar',
  Currencies = 'currencies',
  Prices = 'prices',
  Orderbook = 'orderbook',
  OpenOrders = 'open_orders',
  OpenOrdersCsv = 'open_orders_csv',
  ClosedOrders = 'closed_orders',
  ClosedOrdersCount = 'closed_orders_count',
  ClosedOrdersCsv = 'closed_orders_csv',
  CreateOrder = 'create_order',
  CancelOrder = 'cancel_order',
}

const marketQql = {
  getConfig: () => {
    return gql.query({
      operation: TradeOperations.Config,
      fields: contractsAndTokensListFields,
    });
  },

  getInstruments: () => {
    return gql.query({
      operation: TradeOperations.Instruments,
      fields: getInstrumentsFields,
    });
  },

  getPrices: () => {
    return gql.query({
      operation: TradeOperations.Instruments,
      fields: instrumentsPricesFields,
    });
  },

  getCurrencies: () => {
    return gql.query({
      operation: TradeOperations.Currencies,
      fields: getCurrenciesField,
    });
  },

  // ORDERS
  subscribeOpenOrders: (address: string) => {
    return gql.subscription({
      operation: TradeOperations.OpenOrders,
      variables: {
        address: { value: address, required: true },
      },
      fields: getOrdersFields,
    });
  },

  getOpenOrders: (address: string) => {
    return gql.query({
      operation: TradeOperations.OpenOrders,
      variables: {
        address: { value: address, required: true },
        offset: { value: 0, type: 'Float' },
        limit: { value: 1000, type: 'Float' },
      },
      fields: getOrdersFields,
    });
  },

  getClosedOrders: (address: `0x${string}`, offset?: number, limit?: number) => {
    return gql.query([
      {
        operation: TradeOperations.ClosedOrders,
        variables: {
          address: { value: address, required: true },
          offset: { value: offset || 0, type: 'Float' },
          limit: { value: limit || 10, type: 'Float' },
        },
        fields: getOrdersFields,
      },
      {
        operation: TradeOperations.ClosedOrdersCount,
        variables: {
          address: { value: address, required: true },
        },
      },
    ]);
  },

  downloadClosedOrdersCsv: (address: string) => {
    return gql.query({
      operation: TradeOperations.ClosedOrdersCsv,
      variables: {
        address: { value: address, required: true, type: 'String' },
        offset: { value: 0, type: 'Float' },
        limit: { value: 10000, type: 'Float' },
        time_zone_offset: { value: -new Date().getTimezoneOffset(), type: 'Float' },
      },
    });
  },

  downloadOpenOrdersCsv: (address: string) => {
    return gql.query({
      operation: TradeOperations.OpenOrdersCsv,
      variables: {
        address: { value: address, required: true, type: 'String' },
        offset: { value: 0, type: 'Float' },
        limit: { value: 10000, type: 'Float' },
        time_zone_offset: { value: -new Date().getTimezoneOffset(), type: 'Float' },
      },
    });
  },

  createOrder: (address: string) => {
    return gql.mutation({
      operation: TradeOperations.CreateOrder,
      variables: {
        address: { value: address, required: true },
      },
    });
  },

  cancelOrder: (id: string, address: `0x${string}`) => {
    return gql.mutation({
      operation: TradeOperations.CancelOrder,
      variables: {
        id: { value: id, required: true, type: 'String' },
        address: { value: address, required: true, type: 'String' },
      },
      fields: ['instrument_id'],
    });
  },

  subscribeOrderbook: (instrument_id: string) => {
    return gql.subscription({
      operation: TradeOperations.Orderbook,
      variables: {
        instrument_id: { value: instrument_id, required: true },
      },
      fields: subscribeOrderbookFields,
    });
  },

  subscribeInstrumentPrice: (instrument_id: string) => {
    return gql.subscription({
      operation: TradeOperations.InstrumentPriceBar,
      variables: {
        instrument_id: { value: instrument_id, required: true },
        periodicity: { value: Periodicity.Minute, required: true, type: 'InstrumentHistoryPeriodicity' },
      },
      fields: subscribeInstrumentPriceFields,
    });
  },

  getInstrumentPriceBars: ({ instrument_id, periodicity, limit }: GetInstrumentPriceBarsPayload) => {
    return gql.query({
      operation: TradeOperations.InstrumentPriceBars,
      variables: {
        instrument_id: { value: instrument_id, required: true, type: 'String' },
        periodicity: { value: periodicity, required: true, type: 'InstrumentHistoryPeriodicity' },
        limit: { value: limit, required: true },
      },
      fields: instrumentPriceBarsFields,
    });
  },
};

export default marketQql;
